"use client";
import React from "react";
import {
  ApiResponse,
  NBAPlayer,
  Player,
  Sport,
  StartingQuarterbackResponse,
  TeamBasic,
  TeamColor,
  formatEmptyState,
} from "common";
import useFetch from "@/utils/fetch";
import Card from "@/components/primitives/card";
import Icon from "@/components/primitives/icon";
import { LoadingCircle } from "@/components/primitives/loading";
import NotFound from "@/components/primitives/not-found";
import TeamLogo from "@/components/team/logo";
import { truncateName } from "@/utils/injuries";
import Link from "next/link";
import { createPlayerProfileSlug } from "@/utils/url";
interface GamePlayersToWatch {
  away: PlayersToWatch;
  home: PlayersToWatch;
}
interface PlayersToWatch extends Player {
  jersey: number;
  pointsPerGame: string;
  reboundsPerGame: string;
  assistsPerGame: string;
  fieldGoalPct: string;
  threePointPct: string;
  freeThrowPct: string;
  stealsPerGame: string;
  blocksPerGame: string;
}
export default function PlayersToWatch({
  awayTeam,
  homeTeam,
  sport,
  gameId,
  season
}: {
  awayTeam: TeamBasic & TeamColor;
  homeTeam: TeamBasic & TeamColor;
  sport: Sport;
  gameId: number;
  season: number;
}) {
  const request: string = `${process.env.NEXT_PUBLIC_API_URL}/api/sports/nba/players-to-watch/${gameId}/${season}`;

  const {
    data: clientData,
    isLoading,
    isError,
    error,
  } = useFetch<ApiResponse<GamePlayersToWatch> | null>(null, request, []);

  const data = clientData?.data;
  return (
    <Card
      header={{
        title: (
          <>
            <Icon name="basketball" size="md" className="fill-primary-500" />
            <span>Players to Watch</span>
          </>
        ),
      }}
    >
      <div className="table-container mt-2">
        {isLoading ? (
          <div className="flex justify-center items-center h-[220px]">
            <LoadingCircle />
          </div>
        ) : !isLoading && (!data || isError) ? (
          <NotFound label="Players TBD" />
        ) : (
          data && (
            <div className="table-container mt-2 text-sm-medium text-secondary-300">
              <div className="flex flex-row justify-between">
                {data.away ? (
                  <Link
                    href={createPlayerProfileSlug(sport, data.away.id)}
                    className="flex-basis-6/12 flex-row flex items-center gap-4 uppercase text-left"
                  >
                    <div
                      className="w-12 h-12 border rounded-full flex items-center justify-center"
                      style={{ borderColor: `#${awayTeam.primaryColor}` }}
                    >
                      <TeamLogo team={awayTeam} size="sm" link sport={sport} />
                    </div>
                    <div className="flex flex-col gap-1">
                      <div className="text-title-bold">
                        {truncateName(data.away.firstName)} {data.away.lastName}
                      </div>
                      <div className="text-label-lg text-secondary-400">
                        #{data.away.jersey} {data.away.position}
                      </div>
                    </div>
                  </Link>
                ) : (
                  <div className="flex items-center">
                    {formatEmptyState("tbd")}
                  </div>
                )}
                {data.home ? (
                  <Link
                    href={createPlayerProfileSlug(sport, data.home.id)}
                    className="flex-basis-6/12 flex-row flex items-center gap-4 uppercase text-right"
                  >
                    <div
                      className="w-12 h-12 border rounded-full flex items-center justify-center order-2"
                      style={{ borderColor: `#${homeTeam.primaryColor}` }}
                    >
                      <TeamLogo team={homeTeam} size="sm" link sport={sport} />
                    </div>
                    <div className="flex flex-col gap-1">
                      <div className="text-title-bold">
                        {truncateName(data.home.firstName)} {data.home.lastName}
                      </div>
                      <div className="text-label-lg text-secondary-400">
                        #{data.home.jersey} {data.home.position}
                      </div>
                    </div>
                  </Link>
                ) : (
                  <div className="flex items-center">
                    {formatEmptyState("tbd")}
                  </div>
                )}
              </div>

              <div className="flex flex-row justify-between px-3 h-[46px] items-center rounded-md">
                <div className="flex flex-row gap-1 items-center text-title-bold basis-5/12">
                  {data.away ? (
                    <>{data.away?.pointsPerGame}</>
                  ) : (
                    formatEmptyState("empty")
                  )}
                </div>
                <div className="text-label-lg text-secondary-400 justify-center flex basis-2/12">
                  PTS/G
                </div>
                <div className="flex items-center gap-1 text-title-bold justify-end basis-5/12">
                  {data.home ? (
                    <>{data.home?.pointsPerGame}</>
                  ) : (
                    formatEmptyState("empty")
                  )}
                </div>
              </div>
              <div className="flex flex-row justify-between px-3 h-[46px] items-center rounded-md bg-surface-300">
                <div className="flex flex-row gap-1 items-center text-title-bold basis-5/12">
                  {data.away?.reboundsPerGame || formatEmptyState("empty")}
                </div>
                <div className="text-label-lg text-secondary-400 justify-center flex basis-2/12">
                  REB/G
                </div>
                <div className="flex items-center gap-1 text-title-bold justify-end basis-5/12">
                  {data.home?.reboundsPerGame || formatEmptyState("empty")}
                </div>
              </div>
              <div className="flex flex-row justify-between px-3 h-[46px] items-center rounded-md">
                <div className="flex flex-row gap-1 items-center text-title-bold basis-5/12">
                  {data.away?.assistsPerGame || formatEmptyState("empty")}
                </div>
                <div className="text-label-lg text-secondary-400 justify-center flex basis-2/12">
                  AST/G
                </div>
                <div className="flex items-center gap-1 text-title-bold justify-end basis-5/12">
                  {data.home?.assistsPerGame || formatEmptyState("empty")}
                </div>
              </div>
              <div className="flex flex-row justify-between px-3 h-[46px] items-center rounded-md bg-surface-300">
                <div className="flex flex-row gap-1 items-center text-title-bold basis-5/12">
                  {data.away?.fieldGoalPct || formatEmptyState("empty")}
                </div>
                <div className="text-label-lg text-secondary-400 justify-center flex basis-2/12">
                  FG%
                </div>
                <div className="flex items-center gap-1 text-title-bold justify-end basis-5/12">
                  {data.home?.fieldGoalPct || formatEmptyState("empty")}
                </div>
              </div>
              <div className="flex flex-row justify-between px-3 h-[46px] items-center rounded-md">
                <div className="flex flex-row gap-1 items-center text-title-bold basis-5/12">
                  {data.away?.threePointPct || formatEmptyState("empty")}
                </div>
                <div className="text-label-lg text-secondary-400 justify-center flex basis-2/12">
                  3P%
                </div>
                <div className="flex items-center gap-1 text-title-bold justify-end basis-5/12">
                  {data.home?.threePointPct || formatEmptyState("empty")}
                </div>
              </div>
              <div className="flex flex-row justify-between px-3 h-[46px] items-center rounded-md bg-surface-300">
                <div className="flex flex-row gap-1 items-center text-title-bold basis-5/12">
                  {data.away?.freeThrowPct || formatEmptyState("empty")}
                </div>
                <div className="text-label-lg text-secondary-400 justify-center flex basis-2/12">
                  FT%
                </div>
                <div className="flex items-center gap-1 text-title-bold justify-end basis-5/12">
                  {data.home?.freeThrowPct || formatEmptyState("empty")}
                </div>
              </div>
              <div className="flex flex-row justify-between px-3 h-[46px] items-center rounded-md">
                <div className="flex flex-row gap-1 items-center text-title-bold basis-5/12">
                  {data.away?.stealsPerGame || formatEmptyState("empty")}
                </div>
                <div className="text-label-lg text-secondary-400 justify-center flex basis-2/12">
                  STL/G
                </div>
                <div className="flex items-center gap-1 text-title-bold justify-end basis-5/12">
                  {data.home?.stealsPerGame || formatEmptyState("empty")}
                </div>
              </div>
              <div className="flex flex-row justify-between px-3 h-[46px] items-center rounded-md bg-surface-300">
                <div className="flex flex-row gap-1 items-center text-title-bold basis-5/12">
                  {data.away?.blocksPerGame || formatEmptyState("empty")}
                </div>
                <div className="text-label-lg text-secondary-400 justify-center flex basis-2/12">
                  BLK/G
                </div>
                <div className="flex items-center gap-1 text-title-bold justify-end basis-5/12">
                  {data.home?.blocksPerGame || formatEmptyState("empty")}
                </div>
              </div>
            </div>
          )
        )}
      </div>
    </Card>
  );
}
